import type { ReactNode } from 'react';
import { Pill, primaryButtonClass } from '../points/controls';
import { VersionStatusPill } from '../points/PointsPreviewPane';

/**
 * The catalogue table two features share: points systems, and fantasy salary models.
 *
 * <b>It is one component rather than two that look alike, and that is a requirement rather than a
 * tidy-up.</b> `fantasy-contests.md` §4.1 asks for the salary model catalogue to be the points
 * system catalogue with a different noun — the same status pills, the same shield on a
 * platform-owned row, the same <em>Clone to edit</em> where an Edit control would refuse, the same
 * Archive offered ahead of Delete — on the grounds that an operator who has learned one already
 * knows the other. Two implementations agree until one of them is changed, and the drift is
 * silent: the screens simply stop matching until somebody notices the fantasy one has grown a
 * disabled Edit button.
 *
 * Three rules live here rather than with the callers, because each is a decision
 * `points-systems.md` §2.2/§2.4 made once and neither screen gets to remake:
 *
 * - A row nobody may edit has **no edit control at all**, never a disabled one. A control that
 *   looks available and refuses is worse than an absent one.
 * - **Archive sits before Delete.** For a row something has pinned the server refuses the delete
 *   with a message leading with Archive; nothing here decides that, the row asks and the server
 *   answers.
 * - **A null usage count renders "—", never 0.** "Nothing uses this" and "this screen was not
 *   given the list to count from" send an operator to very different places.
 *
 * Everything that differs between the two features is copy and arrives as props: the heading, the
 * sentence under it, what the usage column counts, and the empty state — which is genuinely
 * different, since one offers a published points ladder to start from and the other a reference
 * pricing model.
 */

export interface CatalogueVersionRow {
  versionNumber: number;
  status: string;
  publishedAt: string | null;
}

/**
 * What the table needs to know about one row, whatever DTO it came from.
 *
 * Read through an accessor rather than by making callers reshape their arrays, so every callback
 * still hands back the caller's own object. The two DTOs spell "cloned from" with different key
 * names (`clonedFromSystemId`, `clonedFromSalaryModelId`) and nothing else about them differs.
 */
export interface CatalogueRowFacts {
  id: string;
  name: string;
  description: string | null;
  isPlatform: boolean;
  isArchived: boolean;
  clonedFromId: string | null;
  versions: readonly CatalogueVersionRow[];
}

export interface CatalogueCopy {
  /** The card's own heading. The pane above it names the section; this names the list. */
  title: string;
  /** The heading of the first column — the singular noun a row is. */
  rowLabel: string;
  blurb: ReactNode;
  newLabel: string;
  /** The usage column's header, and the sentence saying what it counts. */
  usageLabel: string;
  usageHint?: string;
  /** What a row with no versions says in the versions column. */
  emptyVersions?: string;
}

export function CatalogueList<T>({
  items,
  facts,
  copy,
  usage,
  includeArchived,
  onIncludeArchivedChange,
  onNew,
  onOpen,
  onClone,
  onArchive,
  onUnarchive,
  onDelete,
  canManage,
  canCreate = true,
  nameOf,
  notice,
  emptyState,
}: {
  items: readonly T[];
  facts: (item: T) => CatalogueRowFacts;
  copy: CatalogueCopy;
  /**
   * How many things use each row, keyed by id, or null where the client was not given the list to
   * count from. Null renders as "—" rather than 0 — see the note on this file.
   */
  usage: ReadonlyMap<string, number> | null;
  includeArchived: boolean;
  onIncludeArchivedChange: (next: boolean) => void;
  onNew: () => void;
  onOpen: (item: T) => void;
  onClone?: (item: T) => void;
  onArchive: (item: T) => void;
  onUnarchive: (item: T) => void;
  onDelete: (item: T) => void;
  /** False for a platform row seen by a room: visible, usable, never editable. */
  canManage: (item: T) => boolean;
  canCreate?: boolean;
  /** Resolves a cloned-from id to a name, for the provenance line. */
  nameOf?: (id: string) => string | null;
  notice?: ReactNode;
  /** Shown instead of the table when there is nothing in it. */
  emptyState: ReactNode;
}) {
  return (
    <section className="border border-line bg-surface">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h2 className="font-display text-sm font-semibold text-ink-950">{copy.title}</h2>
          <p className="mt-1 max-w-2xl text-xs text-ink-800/60">{copy.blurb}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-ink-800/60">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => onIncludeArchivedChange(e.target.checked)}
              className="accent-[var(--color-felt-700)]"
            />
            Show archived
          </label>
          {canCreate ? (
            <button type="button" onClick={onNew} className={primaryButtonClass}>
              {copy.newLabel}
            </button>
          ) : null}
        </div>
      </header>

      {notice ? <div className="border-b border-line px-5 py-3">{notice}</div> : null}

      {items.length === 0 ? (
        emptyState
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
              <tr className="border-b border-line">
                <th className="px-5 py-2.5 font-semibold">{copy.rowLabel}</th>
                <th className="px-3 py-2.5 font-semibold">Versions</th>
                <th className="px-3 py-2.5 font-semibold" title={copy.usageHint}>
                  {copy.usageLabel}
                </th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const row = facts(item);
                return (
                  <Row
                    key={row.id}
                    item={item}
                    row={row}
                    emptyVersions={copy.emptyVersions ?? 'Nothing built yet'}
                    usage={usage}
                    manageable={canManage(item)}
                    onOpen={onOpen}
                    onClone={onClone}
                    onArchive={onArchive}
                    onUnarchive={onUnarchive}
                    onDelete={onDelete}
                    nameOf={nameOf}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Row<T>({
  item,
  row,
  emptyVersions,
  usage,
  manageable,
  onOpen,
  onClone,
  onArchive,
  onUnarchive,
  onDelete,
  nameOf,
}: {
  item: T;
  row: CatalogueRowFacts;
  emptyVersions: string;
  usage: ReadonlyMap<string, number> | null;
  manageable: boolean;
  onOpen: (item: T) => void;
  onClone?: (item: T) => void;
  onArchive: (item: T) => void;
  onUnarchive: (item: T) => void;
  onDelete: (item: T) => void;
  nameOf?: (id: string) => string | null;
}) {
  const current = newestVersion(row.versions);
  const draft = row.versions.find((v) => v.status === 'draft');
  const published = [...row.versions].reverse().find((v) => v.status === 'published');
  const count = usage?.get(row.id) ?? null;
  const source = row.clonedFromId ? nameOf?.(row.clonedFromId) : null;

  return (
    <tr className={`border-b border-line/60 last:border-b-0 ${row.isArchived ? 'opacity-55' : ''}`}>
      <td className="px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          {row.isPlatform ? <PlatformBadge /> : null}
          <span className="font-medium text-ink-950">{row.name}</span>
          {row.isArchived ? <Pill tone="neutral">Archived</Pill> : null}
        </div>
        {row.description ? (
          <p className="mt-0.5 max-w-lg text-xs text-ink-800/55">{row.description}</p>
        ) : null}
        {/* A clone says where it came from. It is independent from the moment it is made, so this
            is provenance rather than a link — and it survives the source being deleted, which is
            exactly why the id is deliberately not a foreign key. */}
        {row.clonedFromId ? (
          <p className="mt-0.5 text-[11px] text-ink-800/45">
            Cloned from {source ?? 'another one'}
          </p>
        ) : null}
      </td>

      <td className="px-3 py-3">
        {current ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="num text-xs text-ink-800/70">v{current.versionNumber}</span>
            <VersionStatusPill status={current.status} />
            {/* Both are worth showing at once: the published one is what something would pin
                today, the draft is what the next publish would replace it with. */}
            {draft && published && draft.versionNumber !== current.versionNumber ? (
              <span className="num text-[10px] text-ink-800/45">draft v{draft.versionNumber}</span>
            ) : null}
            {published && published.versionNumber !== current.versionNumber ? (
              <span className="num text-[10px] text-ink-800/45">
                published v{published.versionNumber}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="text-xs text-ink-800/40">{emptyVersions}</span>
        )}
      </td>

      <td className="num px-3 py-3 text-ink-800/70">
        {count === null ? <span className="text-ink-800/35">—</span> : count}
      </td>

      <td className="px-5 py-3 text-right">
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => onOpen(item)}
            className="text-xs font-medium text-felt-700 underline underline-offset-2 hover:text-felt-900"
          >
            {manageable ? 'Edit' : 'View'}
          </button>

          {/* Nothing to copy from a row with no versions — the control is absent rather than
              present and inert. */}
          {onClone && current ? (
            <button
              type="button"
              onClick={() => onClone(item)}
              className="text-xs font-medium text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
            >
              {manageable ? 'Duplicate' : 'Clone to edit'}
            </button>
          ) : null}

          {manageable ? (
            row.isArchived ? (
              <button
                type="button"
                onClick={() => onUnarchive(item)}
                className="text-xs font-medium text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
              >
                Restore
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onArchive(item)}
                  className="text-xs font-medium text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
                >
                  Archive
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(item)}
                  className="text-xs font-medium text-ink-800/50 underline underline-offset-2 hover:text-bad"
                >
                  Delete
                </button>
              </>
            )
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/**
 * The shield, matching how platform-controlled tags are already marked: this belongs to the
 * platform, every room can use it, no room can change it.
 */
export function PlatformBadge({ label = 'Platform' }: { label?: string }) {
  return (
    <span
      title="Owned by the platform. Every room can use it; no room can change it."
      className="inline-flex items-center gap-1 border border-line bg-ground px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-800/65"
    >
      <svg viewBox="0 0 12 14" aria-hidden className="size-3 fill-current">
        <path d="M6 0 0 2.2v4.3C0 10 2.6 13.1 6 14c3.4-.9 6-4 6-7.5V2.2L6 0Z" />
      </svg>
      {label}
    </span>
  );
}

/** The version a reader means by "the current one": the highest number, whatever its status. */
export function newestVersion(
  versions: readonly CatalogueVersionRow[],
): CatalogueVersionRow | null {
  return versions.length === 0
    ? null
    : versions.reduce((a, b) => (b.versionNumber > a.versionNumber ? b : a));
}

/** The newest published version — what something starting today would pin. */
export function newestPublished(
  versions: readonly CatalogueVersionRow[],
): CatalogueVersionRow | null {
  const published = versions.filter((v) => v.status === 'published');
  return published.length === 0
    ? null
    : published.reduce((a, b) => (b.versionNumber > a.versionNumber ? b : a));
}
